import type { EnhancedProductFamilyId, EnhancedProductGroup, EnhancedProductVariantId } from '#api/enhancements/enhancedTmsApiTypes'
import type { LocalConfigurationId } from '../slices/configurationSlice'

export const makeOptionChangeSelectionId = () => `change_${window.crypto.randomUUID()}` as OptionChangeSelectionId
export const makeOptionChangeUndoId = () => `undo_${window.crypto.randomUUID()}` as OptionChangeUndoId
export const makeOptionChangeRedoId = () => `redo_${window.crypto.randomUUID()}` as OptionChangeRedoId

export function makeOptionChangeId(kind: 'change'): OptionChangeSelectionId
export function makeOptionChangeId(kind: 'undo'): OptionChangeUndoId
export function makeOptionChangeId(kind: 'redo'): OptionChangeRedoId
export function makeOptionChangeId(kind: 'change' | 'undo' | 'redo') {
  switch (kind) {
    case 'change':
      return makeOptionChangeSelectionId()
    case 'undo':
      return makeOptionChangeUndoId()
    case 'redo':
      return makeOptionChangeRedoId()
  }
}

export type OptionChangeSelectionId = `change_${string}` & { __optionChangeBrand: never }
export type OptionChangeUndoId = `undo_${string}` & { __optionChangeBrand: never }
export type OptionChangeRedoId = `redo_${string}` & { __optionChangeBrand: never }
export type OptionChangeId = OptionChangeSelectionId | OptionChangeRedoId | OptionChangeUndoId

export const isOptionChangeSelectionId = (id: OptionChangeId): id is OptionChangeSelectionId => id.startsWith('change_')
export const isOptionChangeUndoId = (id: OptionChangeId): id is OptionChangeUndoId => id.startsWith('undo_')
export const isOptionChangeRedoId = (id: OptionChangeId): id is OptionChangeRedoId => id.startsWith('redo_')

interface Modification {
  family: ModificationFamily
}

interface ModificationFamily {
  id: EnhancedProductFamilyId
  title?: string
}

interface ModificationVariant {
  id: EnhancedProductVariantId
  title: string
  price: number
}

export interface Removal extends Modification {
  archetype: 'removal'
  fromVariant: ModificationVariant
}

export interface Addition extends Modification {
  archetype: 'addition'
  toVariant: ModificationVariant
}

export interface Change extends Modification {
  archetype: 'change'
  fromVariant: ModificationVariant
  toVariant: ModificationVariant
}

export type ValidModifications = Removal | Addition | Change

export interface OptionChangeConsequence {
  priceDelta: number
  priceSum: number
  additions: Addition[]
  changes: Change[]
  removals: Removal[]
}

export type OptionSelection = {
  family: ModificationFamily
  fromVariant: ModificationVariant
  wasUserSelectedVariant: boolean
  toVariant: ModificationVariant
}
export type OptionDeselection = {
  family: ModificationFamily
  fromVariant: ModificationVariant
  wasUserSelectedVariant: boolean
}

export type OptionChange = {
  process: OptionChangeId
  started: Timestamp
  config: LocalConfigurationId
  status: 'queued' | 'pending' | 'success' | 'failed-unresolved' | 'failed-resolved'
}

export type OptionChangeSelection = OptionChange & {
  process: OptionChangeSelectionId
  type: 'change'
  optionSource: 'ecm' | 'national-code'
  selections: OptionSelection[]
  deselections: OptionDeselection[]
}
export type OptionChangeUndo = OptionChange & {
  process: OptionChangeUndoId
  type: 'undo'
  optionSource: 'ecm' | 'national-code'
  // undo is only allowed to handle a prior change selection
  sourceChange: OptionChangeSelectionId
  /**
   * Passing are changes that have to be applied. The producer of this event has to invert the changes
   * (so, a "normal" change to (fromVariant -> toVariant), this is then in order of (toVariant -> fromVariant) change)
   */
  changes: Change[]
  /**
   * **Careful**, selections are the original data entry, meaning that they go from the previous variant to current variant
   * therefore undoing means going from `toVariant` -> `fromVariant`
   */
  selections: OptionSelection[]
  deselections: OptionDeselection[]
}
export type OptionChangeRedo = OptionChange & {
  process: OptionChangeRedoId
  type: 'redo'
  optionSource: 'ecm' | 'national-code'
  // redo is only allowed to handle a prior undoing
  redoingChange: OptionChangeUndoId
  sourceChange: OptionChangeSelectionId
  selections: OptionSelection[]
  deselections: OptionDeselection[]
}

export type OptionChangeQuery = OptionChangeSelection | OptionChangeUndo | OptionChangeRedo

export type OptionChangeQueued = OptionChangeQuery & { status: 'queued' }
export type OptionChangeSelectionQueued = OptionChangeSelection & { status: 'queued' }
export type OptionChangeUndoQueued = OptionChangeUndo & { status: 'queued' }
export type OptionChangeRedoQueued = OptionChangeRedo & { status: 'queued' }

export type OptionChangePending = OptionChangeQuery & { status: 'pending'; request: { id: string; started: Timestamp } }
export type OptionChangeSelectionPending = OptionChangeSelection & { status: 'pending'; request: { id: string; started: Timestamp } }
export type OptionChangeUndoPending = OptionChangeUndo & { status: 'pending'; request: { id: string; started: Timestamp } }
export type OptionChangeRedoPending = OptionChangeRedo & { status: 'pending'; request: { id: string; started: Timestamp } }

export type OptionChangeSucceeded = OptionChangeQuery & {
  status: 'success'
  ended: Timestamp
  conflicts: ((Removal | Change) & { lastSelectionTime: Timestamp })[]
  consequences: OptionChangeConsequence
}
export type OptionChangeSelectionSucceeded = OptionChangeSelection & {
  status: 'success'
  ended: Timestamp
  conflicts: ((Removal | Change) & { lastSelectionTime: Timestamp })[]
  consequences: OptionChangeConsequence
}
export type OptionChangeUndoSucceeded = OptionChangeUndo & {
  status: 'success'
  ended: Timestamp
  conflicts: ((Removal | Change) & { lastSelectionTime: Timestamp })[]
  consequences: OptionChangeConsequence
}
export type OptionChangeRedoSucceeded = OptionChangeRedo & {
  status: 'success'
  ended: Timestamp
  conflicts: ((Removal | Change) & { lastSelectionTime: Timestamp })[]
  consequences: OptionChangeConsequence
}

export type OptionChangeFailedUnresolved = OptionChangeQuery & { status: 'failed-unresolved'; ended: Timestamp; error: unknown }
export type OptionChangeSelectionFailedUnresolved = OptionChangeSelection & { status: 'failed-unresolved'; ended: Timestamp; error: unknown }
export type OptionChangeUndoFailedUnresolved = OptionChangeUndo & { status: 'failed-unresolved'; ended: Timestamp; error: unknown }
export type OptionChangeRedoFailedUnresolved = OptionChangeRedo & { status: 'failed-unresolved'; ended: Timestamp; error: unknown }

export type OptionChangeFailedResolved = OptionChangeQuery & { status: 'failed-resolved'; ended: Timestamp; error: unknown }
export type OptionChangeSelectionFailedResolved = OptionChangeSelection & { status: 'failed-resolved'; ended: Timestamp; error: unknown }
export type OptionChangeUndoFailedResolved = OptionChangeUndo & { status: 'failed-resolved'; ended: Timestamp; error: unknown }
export type OptionChangeRedoFailedResolved = OptionChangeRedo & { status: 'failed-resolved'; ended: Timestamp; error: unknown }

export type AnySucceededOptionChange = OptionChangeSelectionSucceeded | OptionChangeUndoSucceeded | OptionChangeRedoSucceeded | OptionChangeSucceeded

export function isOptionChangeSucceeded(change: OptionChangeEntry): change is AnySucceededOptionChange {
  return change.status === 'success'
}

const subGroups = (group: EnhancedProductGroup): EnhancedProductGroup[] => [group, ...(group.subGroups ?? []).flatMap(subGroups)]

export type OptionChangeEntry =
  | OptionChangeQueued
  | OptionChangePending
  | OptionChangeSucceeded
  | OptionChangeFailedUnresolved
  | OptionChangeFailedResolved
export type OptionChangeSelectionEntry =
  | OptionChangeSelectionQueued
  | OptionChangeSelectionPending
  | OptionChangeSelectionSucceeded
  | OptionChangeSelectionFailedUnresolved
  | OptionChangeSelectionFailedResolved
export type OptionChangeUndoEntry =
  | OptionChangeUndoQueued
  | OptionChangeUndoPending
  | OptionChangeUndoSucceeded
  | OptionChangeUndoFailedUnresolved
  | OptionChangeUndoFailedResolved
export type OptionChangeRedoEntry =
  | OptionChangeRedoQueued
  | OptionChangeRedoPending
  | OptionChangeRedoSucceeded
  | OptionChangeRedoFailedUnresolved
  | OptionChangeRedoFailedResolved
